/** Live settings panels supply their own titles, grouping and form priorities.
 * Common groups have shared labels; custom groups remain accessible under the
 * name the plugin declared. A settings-slot panel without a group uses 其他设置.
 */

import type { PanelEntry } from "../lib/api";
import type { IconName } from "../components/Icon";

/** Where a setting lives, in the order an owner reads them. */
export interface SettingsGroup {
  key: string;
  title: string;
  note: string;
  icon: IconName;
  order: number;
}

/** The group a destination lands in when its plugin has not said. */
export const OTHER_GROUP = "other";

/**
 * One glyph for each of the eight groups, so the list can be read by shape as well as by name: the model, the
 * abilities, memory, boundaries, production, channels, usage, and the developer's own corner. Keyed by the word
 * contributors agree on, never by a plugin; a group nobody gave a glyph to keeps the gear.
 */
const GROUP_ICONS: Record<string, IconName> = {
  model: "brain", tools: "tool", memory: "memory", rules: "shield",
  production: "clapperboard", accounts: "channels", operation: "chart", development: "code",
};

/** The glyph of a group, for the rows under its name. */
export function groupIcon(key: string): IconName {
  return GROUP_ICONS[key] ?? "settings";
}

/** What the entries of a group are called when most of them are folded away. */
export function moreLabel(group: string): string {
  return group === "accounts" ? "更多渠道" : "更多设置";
}

/** `PanelSpec.metadata.settings`, as declared. Every field is optional. */
export interface SettingsDeclaration {
  /** Agent-wide choices or capability-owned configuration; never inferred from a plugin name. */
  scope?: "agent" | "capability";
  title?: string;
  setup_steps?: string[];
  presets?: { label: string; values: Record<string, unknown> }[];
  /** Read-only value/registry labels for reviewing proposed settings. */
  review?: Record<string, unknown>;
  note?: string;
  group?: string;
  group_title?: string;
  group_note?: string;
  group_order?: number;
  order?: number;
  /** Low-frequency controls remain available outside the ordinary directory. */
  advanced?: boolean;
  /** Related preferences share one product entrance; each namespace still owns its values. */
  collection?: { key: string; title: string; note?: string; order?: number } | null;
  /** This page reports a live check of its own; `default_namespace` is where
   *  the chosen default is stored and `default_field` the field there that
   *  holds it; `fallback_field` the field there that holds the models tried,
   *  in order, when the chosen one fails; `supplied_level_field` the choice field there that holds the
   *  owner's own level (how hard to think) for the models somebody else supplies, drawn where the model is
   *  chosen while there are such models. Declared by the plugin that owns the check. */
  readiness?: {
    default_namespace?: string; default_field?: string; fallback_field?: string; supplied_level_field?: string;
  } | null;
  /** Field key -> the row-group columns a reader sees first. */
  primary_fields?: Record<string, string[]> | null;
  /** Top-level fields shown first, in order. Others fold away unless required. */
  common_fields?: string[] | null;
  /** Ordinary tariff controls; the service declares exactly which billing units it accepts. */
  pricing_fields?: Record<string, { units: Record<string, string> }>;
  /**
   * Field key -> what an empty box on this page actually means.
   *
   * Declared by the plugin that resolves the value, because it is the only one
   * that can say. `note` is the sentence to show instead of asking again;
   * `label` is what choosing none is called where it is one of the options.
   * Which model an empty default calls is not a rule here: the model's own
   * registration says it stands in (`modelSettlement`). A reader that decided
   * either for itself would be a second answer to a question the plugin already
   * answers every turn, and the two would disagree the first time one of them
   * changed (F7, 2026-10-27: two models and no default).
   */
  when_unset?: Record<string, { note?: string; label?: string }> | null;
  /**
   * The services the platform may supply this page's plugin with.  Whether it
   * does, for this Agent right now, is the platform's own answer in the
   * namespace's values (`platform_enabled`), written only where this is
   * declared.
   */
  platform_supply?: { protocol?: string }[] | null;
  /** A second way in, at a step of the journey the shell already draws. */
}

/** One place to go inside IP 设置. */
export interface SettingsDestination {
  /** The `?namespace=` value: a config namespace, or `panel:<key>`. */
  key: string;
  title: string;
  note: string;
  group: string;
  groupOrder?: number;
  order: number;
  /** The namespace this destination configures, when it is a config page. */
  namespace?: string;
  /** The panel this destination renders, when it is a panel of its own. */
  panelKey?: string;
  /** Whose settings these are: the namespace configured, else the panel's
   *  own plugin. What a link naming 「that plugin's settings」 resolves by. */
  owner: string;
  /** The live panel revision; cached settings reads must follow withdrawal/reload. */
  generationId?: PanelEntry["generation_id"];
  /** The glyph the page declared for itself, if it did; the list draws its group's otherwise. */
  icon?: string;
  declared: SettingsDeclaration;
}

function declaredSettings(panel: PanelEntry): SettingsDeclaration | null {
  const value = panel.settings;
  return value && typeof value === "object" ? value as SettingsDeclaration : null;
}

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Which panels are a place to go inside IP 设置.
 *
 * Two ways in, and both are the panel's own word: `slot: settings`, or a
 * `settings` declaration on any slot. The second is what gets
 * `memory_timeline.status` -- a `slot: main` page whose subject is what will be
 * remembered -- to sit next to the retention setting without the shell naming
 * it (it did, by key, before D380).
 */
export function settingsPanels(panels: PanelEntry[]): PanelEntry[] {
  return panels.filter(panel => panel.slot === "settings" || declaredSettings(panel) !== null);
}

/**
 * Every settings destination, in the order the page draws them.
 *
 * A panel carrying `configures` is a generated config page: the destination is
 * the namespace, because that is what `/api/connections` and every link to a
 * setting already use. Anything else is a panel in its own right. `extra` is
 * what the workbench itself keeps (`WorkbenchSettings.tsx`), filed by the same
 * rules.
 */
export function settingsDestinations(panels: PanelEntry[], extra: SettingsDestination[] = []): SettingsDestination[] {
  const rows = settingsPanels(panels).map<SettingsDestination>(panel => {
    const declared = declaredSettings(panel) ?? {};
    const namespace = text(panel.configures);
    return {
      key: namespace || `panel:${panel.key}`,
      title: text(declared.title) || panel.title,
      note: text(declared.note),
      group: text(declared.group) || OTHER_GROUP,
      groupOrder: typeof declared.group_order === "number" ? declared.group_order : 1000,
      order: typeof declared.order === "number" ? declared.order : panel.order ?? 1000,
      ...(namespace ? { namespace } : { panelKey: panel.key }),
      owner: namespace || panel.plugin,
      generationId: panel.generation_id,
      ...(text(panel.icon) ? { icon: text(panel.icon) } : {}),
      declared,
    };
  }).concat(extra.map(row => ({ ...row })));
  // A group is a heading an owner reads, so it exists only where somebody
  // declared what to call it. `group` is the key contributors agree on and
  // `group_title` is the name; a group with the first and not the second has
  // no name, and the shell has none to give it -- writing one would be this
  // console deciding what a plugin's grouping means (the table of nineteen
  // rewritten titles, D380). So its members fall in with 其他设置, which is
  // named, rather than standing under their own key (`accounts`, `rules`,
  // `model`, `memory` stood there in front of an owner, r26a's real browser).
  const named = new Set(rows.filter(row => text(row.declared.group_title)).map(row => row.group));
  for (const row of rows) {
    if (row.group === OTHER_GROUP || named.has(row.group)) continue;
    row.group = OTHER_GROUP;
    row.groupOrder = 1000;
  }
  return rows.sort(compareDestinations);
}

/** A live scene links to existing configuration owners; several scenes may share the same namespace. */
export interface WorkspaceSettingsDestination {
  panel: PanelEntry;
  namespaces: string[];
}

export function workspaceSettingsDestinations(
  panels: PanelEntry[], destinations: SettingsDestination[], availableNamespaces: string[],
): WorkspaceSettingsDestination[] {
  const available = new Set(availableNamespaces);
  const actual = new Set(destinations.flatMap(row => row.namespace && available.has(row.namespace) ? [row.namespace] : []));
  const candidates = panels.flatMap(panel => {
    const declared = panel.workspace?.settings_for;
    if (panel.audience !== "owner" || !panel.generation_id || !Array.isArray(declared)) return [];
    const namespaces = [...new Set(declared.filter((name): name is string =>
      typeof name === "string" && actual.has(name)))];
    return namespaces.length ? [{ panel, namespaces }] : [];
  });
  return candidates;
}

/** Titles are the owner's Chinese words, ordered by pinyin on every machine, not by each machine's default language. */
const TITLE_ORDER = "zh-CN";

/** Group order first, then the order each plugin declared, then the title. */
export function compareDestinations(
  a: { group: string; groupOrder?: number; order: number; title: string },
  b: { group: string; groupOrder?: number; order: number; title: string },
): number {
  return (a.groupOrder ?? 1000) - (b.groupOrder ?? 1000) || a.group.localeCompare(b.group) || a.order - b.order || a.title.localeCompare(b.title, TITLE_ORDER);
}

/** Only nonempty groups, including plugin-defined groups. */
export function settingsGroups(destinations: SettingsDestination[]) {
  const keys = [...new Set(destinations.map(item => item.group))];
  return keys.map(key => settingsGroup(key, destinations)).sort((a, b) =>
    a.order - b.order || a.title.localeCompare(b.title, TITLE_ORDER));
}

/**
 * What one group is called: the name a contributor gave it, or 其他设置.
 *
 * Whoever declared `group_title` names it -- not whichever member happens to
 * sort first, which is what this read before and is how a group of thirteen
 * channels could have rendered as `accounts` because the one member that
 * declared no title sorted ahead of the twelve that did. The key is never
 * drawn: it is the word contributors coordinate on, not a word for an owner
 * (§7.183), and `settingsDestinations` has already moved an unnamed group's
 * members into 其他设置 so no two headings can read the same.
 */
export function settingsGroup(key: string, destinations: SettingsDestination[] = []): SettingsGroup {
  const members = destinations.filter(item => item.group === key).sort((a, b) => a.owner.localeCompare(b.owner));
  const source = members.find(item => text(item.declared.group_title))?.declared ?? members[0]?.declared;
  return { key, title: text(source?.group_title) || "其他设置",
    note: text(source?.group_note), order: source?.group_order ?? 1000, icon: GROUP_ICONS[key] ?? "settings" };
}

/**
 * What a namespace is called, for a page that has only its name.
 *
 * Four pages say 「这次要改的是哪几组设置」 and hold nothing but namespaces;
 * they used a table of nineteen rewritten titles. Now they ask the directory,
 * and a namespace nobody declared a title for keeps the caller's fallback.
 */
export function settingsTitle(
  destinations: SettingsDestination[],
  key: string,
  fallback: string,
): string {
  return destinations.find(item => item.key === key)?.title || fallback;
}

/**
 * The destination a link asked for, when the link had only a name.
 *
 * Most links name a destination outright. One does not: the capabilities page
 * says 「配置这个渠道」 and holds the plugin's name, because that is what a
 * capability entry carries -- and a plugin's settings may be a generated config
 * page, or a page the plugin wrote itself, or both. The shell used to answer
 * this from its table, and got it wrong in the one case the table did not cover
 * (D288: the first press landed on 「这组设置尚未提供入口」). Now the
 * declarations answer, and the first page that plugin declared is where it goes.
 */
export function resolveDestination(
  destinations: SettingsDestination[],
  requested: string,
): string | undefined {
  if (!requested) return undefined;
  const exact = destinations.find(item => item.key === requested);
  return (exact ?? destinations.find(item => item.owner === requested))?.key;
}

/**
 * Where IP 设置 opens when nothing said which part of it to open.
 *
 * The first destination of the first group that has one. Four pages wrote
 * `llm_providers` for this and meant 「the first entry point」; the list is no
 * longer in the shell, so neither is the answer.
 */
export function firstSettingsDestination(destinations: SettingsDestination[]): string | undefined {
  return destinations[0]?.key;
}
